import React, { useState, useEffect } from 'react';

import { Box, InputAdornment, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SearchIcon from '@mui/icons-material/Search';

import EmployeeService from 'services/EmployeeService';
import showNotification from 'commons/helpers/showNotification';
import UserService from 'services/UserService';
import CourtService from 'services/CourtService';
import DebouncedTextInput from 'components/inputs/DebouncedTextInput';

import LabelStatus from 'components/labels/LabelStatus';
import ConfirmationDialog from 'components/modals/ConfirmationDialog';
import CourtListTable from './CourtListTable';
import CourtListToolbar from './CourtListToolbar';
import CourtFormModal from './CourtFormModal';
import { getAgendaColorOrNull } from 'commons/helpers/agendaTheme';
import ButtonStatus from 'components/buttons/ButtonStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PlanUpgradeViaWhatsModal from 'views/employees/PlanUpgradeViaWhatsModal';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  content: {
    marginTop: theme.spacing(1),
  },
  filterBar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  labelNumberOfUsers: {
    marginRight: theme.spacing(1),
  },
  inputSearchName: { minWidth: 450, marginLeft: '10px' },
  iconSearch: { color: '#b3b3b3' },
  outlinedInput: {
    padding: '11px 14px !important',
  },
}));

// COMPONENT
function EmployeesListPage() {
  const { classes } = useStyles();

  const [modalCreate, setModalCreate] = useState({ open: false });
  const [modalDelete, setModalDelete] = useState({ open: false, objectToDelete: null });
  const [modalFutureAppointmentsDeleteConfirm, setModalFutureAppointmentsDeleteConfirm] = useState({
    open: false,
  });
  const [isPreparingDelete, setIsPreparingDelete] = useState(false);

  const [isLoading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [nameSearchTerm, setNameSearchTerm] = useState('');

  const [companyInfo, setCompanyInfo] = useState();
  const [modalUpgradeOpen, setModalUpgradeOpen] = useState(false);

  useEffect(() => {
    async function initData() {
      setLoading(true);

      try {
        const [companyInfoResp, dataResp] = await Promise.all([
          UserService.getMyCompany(),
          CourtService.findAllForCurrentUser(),
        ]);
        setDataSource(
          dataResp.map((curData) => {
            const colorObj = getAgendaColorOrNull(curData.colorId);
            return { ...curData, color: colorObj == null ? 'transparent' : colorObj.header };
          }),
        );
        setCompanyInfo(companyInfoResp);
      } finally {
        setLoading(false);
      }
    }

    initData();
  }, []);

  const handleRefreshList = () => {
    setLoading(true);
    CourtService.findAllForCurrentUser()
      .then((dataResp) => {
        setDataSource(
          dataResp.map((curData) => {
            const colorObj = getAgendaColorOrNull(curData.colorId);
            return { ...curData, color: colorObj == null ? 'transparent' : colorObj.header };
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEdit = (editObj) => {
    setModalCreate({ open: true, objectId: editObj.id });
  };

  const handleDelete = async (objectToDelete) => {
    setModalDelete({ open: true, objectToDelete });
  };

  const handleDeleteConfirmation = () => {
    setIsPreparingDelete(true);

    return EmployeeService.hasFutureAppointments(modalDelete.objectToDelete.id)
      .then((containsFutureAppointments) => {
        if (containsFutureAppointments) {
          setModalDelete((prev) => ({ ...prev, open: false }));
          setModalFutureAppointmentsDeleteConfirm({
            open: true,
            objectToDelete: modalDelete.objectToDelete,
          });
        } else {
          return EmployeeService.delete(modalDelete.objectToDelete).then(() => {
            setDataSource(dataSource.filter((obj) => obj.id !== modalDelete.objectToDelete.id));
            showNotification('success', 'Deletado com sucesso.');
            setModalDelete((prev) => ({ ...prev, open: false }));
          });
        }
      })
      .finally(() => {
        setIsPreparingDelete(false);
      });
  };

  const handleDeleteConfirmWithFutureAppointments = () => {
    setLoading(true);
    return EmployeeService.delete(modalDelete.objectToDelete)
      .then(() => {
        setDataSource(dataSource.filter((obj) => obj.id !== modalDelete.objectToDelete.id));
        showNotification('success', 'Deletado com sucesso.');
        setModalFutureAppointmentsDeleteConfirm((prev) => ({ ...prev, open: false }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdate = () => {
    setModalCreate((prev) => ({ ...prev, open: false }));
    handleRefreshList();
  };

  // computed
  const filteredData = React.useMemo(() => {
    return dataSource.filter((data) => {
      return (
        nameSearchTerm.trim().length === 0 || data.name.toLowerCase().includes(nameSearchTerm.toLowerCase())
      );
    });
  }, [dataSource, nameSearchTerm]);

  const numberOfUsersUsed = dataSource?.length ?? 0;
  const numberOfTotalUsers = companyInfo?.maxNumberUsers;
  const hasUsedAllUsers = numberOfUsersUsed === numberOfTotalUsers;

  return (
    <div className={classes.root}>
      {/* CONFIRM DELETE MODALS */}
      <ConfirmationDialog
        title="Exclusão"
        message={
          <>
            Ao deletar uma quadra, ela ficará indisponível. O histórico é mantido, porém agendamentos futuros
            serão deletados.
            <br />
            <br />
            Tem certeza que deseja deletar?
          </>
        }
        open={modalDelete.open}
        onConfirm={handleDeleteConfirmation}
        onClose={() => {
          setModalDelete((prev) => ({ ...prev, open: false }));
        }}
        loading={isPreparingDelete}
      />
      <ConfirmationDialog
        title="Aviso"
        message={
          <>
            <LabelStatus status="error">
              A quadra possui agendamentos futuros.
              <br />
              <b>Ao deletar a quadra os agendamentos futuros serão removidos.</b>
            </LabelStatus>
            <br />
            Tem certeza que deseja deletar?
          </>
        }
        open={modalFutureAppointmentsDeleteConfirm.open}
        onConfirm={handleDeleteConfirmWithFutureAppointments}
        confirmButtonColor="secondary"
        onClose={() => {
          setModalFutureAppointmentsDeleteConfirm((prev) => ({ ...prev, open: false }));
        }}
      />
      {/* CONFIRM DELETE MODALS */}

      {/* UPDATE MODAL */}
      <PlanUpgradeViaWhatsModal
        open={modalUpgradeOpen}
        onClose={() => {
          setModalUpgradeOpen(false);
        }}
      />

      <CourtFormModal
        open={modalCreate.open}
        onClose={() => {
          setModalCreate((prev) => ({ ...prev, open: false }));
        }}
        onSaveCallback={handleUpdate}
        courtId={modalCreate.objectId}
      />

      <CourtListToolbar
        onAdd={() => {
          setModalCreate({ open: true, objectId: null });
        }}
        isLoading={isLoading}
        disabled={isLoading}
      />

      <Paper className={classes.content} elevation={1}>
        <div className={classes.filterBar}>
          <DebouncedTextInput
            onChange={setNameSearchTerm}
            placeholder="Pesquisar por nome..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className={classes.iconSearch} />
                </InputAdornment>
              ),
              classes: { input: classes.outlinedInput },
            }}
            className={classes.inputSearchName}
            variant="outlined"
          />

          <Box display="flex" flexDirection="row" alignItems="center">
            <LabelStatus
              variant="body2"
              status={hasUsedAllUsers ? 'warning' : null}
              className={classes.labelNumberOfUsers}
            >
              {numberOfUsersUsed} de {numberOfTotalUsers} quadras/usuários
            </LabelStatus>

            <ButtonStatus
              size="small"
              status="success"
              variant="outlined"
              onClick={() => {
                setModalUpgradeOpen(true);
              }}
            >
              <FontAwesomeIcon icon="angles-up" fixedWidth />
              Solicitar Upgrade
            </ButtonStatus>
          </Box>
        </div>
        <CourtListTable
          dataSource={filteredData}
          onDelete={handleDelete}
          onSelect={(row) => {
            handleEdit(row.original);
          }}
          onEdit={handleEdit}
        />
      </Paper>
    </div>
  );
}

export default EmployeesListPage;
